<script setup>
import EmptyState from '@/components/ui/EmptyState.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconSearch from '@/components/icon/IconSearch.vue'

/**
 * The "your search matched nothing" state for a listing page.
 *
 * Distinct from the page's onboarding placeholder on purpose: the user still
 * has content, the query just missed it, so the list controls (and the search
 * box) stay where they are and this explains the empty grid underneath and
 * offers the one-tap way out.
 */
const props = defineProps({
	/** The query that matched nothing, echoed back so the typo is visible. */
	query: { type: String, default: '' },
	/** Plural noun for the title, e.g. "streams", "playlists". */
	noun: { type: String, default: 'items' },
})

const emit = defineEmits({ clear: () => true })
</script>

<template>
	<EmptyState
		:title="`No ${noun} match your search`"
		min-height="40vh"
		role="status"
		data-testid="search-empty"
	>
		<template #icon>
			<icon-base fill="none" :width="22" :height="22" class="text-surface-7">
				<icon-search />
			</icon-base>
		</template>
		<template #subtitle>
			Nothing named “{{ query.trim() }}”. Check the spelling or try a shorter name.
		</template>
		<template #actions>
			<FormButton type="secondary" size="md" @click="emit('clear')">Clear search</FormButton>
		</template>
	</EmptyState>
</template>
